import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from '../api';
import type { User } from '../types';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (chatId: number) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const storedChatId = localStorage.getItem('chat_id');
    if (storedChatId) {
      authenticate(parseInt(storedChatId, 10));
    } else {
      setLoading(false);
    }
  }, []);

  const authenticate = async (_chatId: number) => {
    try {
      setLoading(true);
      setError(null);

      // Get chat_id from localStorage or Telegram
      const storedChatId = localStorage.getItem('chat_id');
      const chatIdToUse = storedChatId ? parseInt(storedChatId, 10) : _chatId;

      if (!chatIdToUse) {
        setLoading(false);
        return;
      }

      // Get users and find the one matching chat_id
      const users = await api.getUsers();

      if (users.length > 0) {
        // Try to find user by telegram_chat_id first
        let matchedUser = users.find(u => u.telegram_chat_id === chatIdToUse);

        // If not found by telegram_chat_id, check if Telegram provides user info
        if (!matchedUser) {
          const telegramUser = (window as unknown as { Telegram?: { WebApp?: { initDataUnsafe?: { user: { id: number; first_name: string; last_name?: string; username?: string } } } } }).Telegram?.WebApp?.initDataUnsafe?.user;
          if (telegramUser) {
            matchedUser = users.find(u => u.telegram_chat_id === telegramUser.id);
            if (matchedUser) {
              setUser({
                ...matchedUser,
                id: telegramUser.id,
                first_name: telegramUser.first_name,
                last_name: telegramUser.last_name ?? null,
                username: telegramUser.username ?? null,
              });
              setLoading(false);
              return;
            }
          }
        }

        if (matchedUser) {
          setUser(matchedUser);
        } else {
          // No match found - user not registered
          setError('Вы не зарегистрированы в системе');
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const login = async (chatId: number) => {
    localStorage.setItem('chat_id', String(chatId));
    await authenticate(chatId);
  };

  const logout = () => {
    localStorage.removeItem('chat_id');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, error, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
